// useReportPostMutation.js
import { useQueryClient } from 'react-query';
import QUERY_CACHE_KEY from '@src/constants/QueryCacheKey';
import FeedDetailedCached from '@src/utils/feed-detailed-cache';
import useApiMutation from './useApiMutation';

function useReportPostMutation(postId, options = {}) {
  const qc = useQueryClient();

  return useApiMutation('post', `/v1/posts/${postId}/report`, {
    async onMutate(...args) {
      await qc.cancelQueries();

      const feedCached = FeedDetailedCached.getFeedDetailedCached();

      let previousData = null;
      feedCached.forEach(([, queryData]) => {
        if (queryData?.pages) {
          queryData.pages.forEach((page) => {
            const foundItem = page.items.find((item) => {
              return item.type === 'post' && item.value.id === postId;
            });

            if (foundItem) {
              previousData = foundItem.value;
            }
          });
        }
      });

      // 1) Optimistic: post courant
      FeedDetailedCached.updateFeedDetailedCached('post', { ...previousData, treatment: 'pending' });
      qc.setQueryData(QUERY_CACHE_KEY.post(postId), (old) => (old ? { ...old, treatment: 'pending' } : old));

      // 2) Optimistic: tous les feeds en cache (avec ou sans query param)
      const allQueries = qc.getQueryCache().getAll();
      allQueries.forEach((q) => {
        const key = q.queryKey;
        if (Array.isArray(key) && key[0] === 'v1' && key[1] === 'feed') {
          qc.setQueryData(key, (old) => {
            if (!old || !old.items) return old;
            return {
              ...old,
              items: old.items.map((it) => (it.id === postId ? { ...it, treatment: 'pending' } : it)),
            };
          });
        }
      });

      if (options.onMutate) return options.onMutate(...args);

      return undefined;
    },

    async onSettled(data, error, variables, context) {
      // 3) Refetch: post + tous les feeds
      await qc.invalidateQueries(QUERY_CACHE_KEY.feedDetailed());
      await qc.invalidateQueries(QUERY_CACHE_KEY.post(postId));
      await qc.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'v1' && q.queryKey[1] === 'feed',
      });

      if (options.onSettled) return options.onSettled(data, error, variables, context);

      return undefined;
    },

    ...options,
  });
}

export default useReportPostMutation;
